/** @file animation.setCurve batches: one call per timeline over the selected keys that still exist. */
import type { CommandCall, Curve, Doc } from '@pose2d/format/types';
import type { KeyRef } from '../../stores/types';
import { buildCurveTrack } from './buildCurveTrack';
import type { CurveRef } from './curveTypes';
import { setCurveCall } from './setCurveCall';
import { toCurveRef } from './toCurveRef';

/** Selected keys grouped by timeline, in first-seen order. */
function groupKeys(keys: readonly KeyRef[]): { ref: CurveRef; frames: Set<number> }[] {
  const groups = new Map<string, { ref: CurveRef; frames: Set<number> }>();
  for (const key of keys) {
    const ref = toCurveRef(key);
    if (ref === null) continue;
    const id = `${ref.animation}|${ref.channel}|${ref.target}`;
    const group = groups.get(id) ?? { ref, frames: new Set<number>() };
    group.frames.add(key.frame);
    groups.set(id, group);
  }
  return [...groups.values()];
}

/**
 * One `animation.setCurve` per (animation, channel, target) with its frames ascending, to send as one batch (one
 * undo step). Discrete keys and keys the document no longer holds are left out; no key left means no call.
 */
export function curveSetCalls(doc: Doc, keys: readonly KeyRef[], curve: Curve): CommandCall[] {
  return groupKeys(keys).flatMap(({ ref, frames }) => {
    const present = new Set(buildCurveTrack(doc, ref)?.frames ?? []);
    const kept = [...frames].filter((f) => present.has(f)).sort((a, b) => a - b);
    return kept.length === 0 ? [] : [setCurveCall(ref, kept, curve)];
  });
}
